import React from 'react';
import { Link } from 'react-router-dom';
import { crafterUrl } from '../utils/urls';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { motion } from 'framer-motion';
import { 
  Package, 
  CheckCircle2, 
  History, 
  ShoppingBag,
  MessageSquare,
  Star,
  Calendar,
  Euro,
  Truck,
  Loader2,
  XCircle
} from 'lucide-react';
import { CrystalTracker } from './CrystalTracker';
import OrderHelpBlock from './OrderHelpBlock';

export default function OrdersSectionTabs({
  activeOrders,
  completedOrders,
  ordersView,
  setOrdersView,
  renderCartOrderCard,
  getOrderStatusLabel,
  getOrderStatusColor,
  getTimelineLabel,
  handleMarkDelivered,
  handleCompleteOrder,
  handleOpenReviewDialog,
  confirmingDelivery,
  reviewedProducts = [] // Array of product IDs that have been reviewed
}) {
  return (
    <div>
      {/* Sub-navigation pills for order sections */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setOrdersView('active')}
          data-testid="orders-tab-active"
          className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
            ordersView === 'active'
              ? 'bg-[#008080] text-white shadow-lg shadow-[#008080]/25'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <Package className="w-4 h-4" />
          In Corso
          {activeOrders.length > 0 && (
            <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
              ordersView === 'active' ? 'bg-white/20' : 'bg-[#008080] text-white'
            }`}>
              {activeOrders.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setOrdersView('completed')}
          data-testid="orders-tab-completed"
          className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
            ordersView === 'completed'
              ? 'bg-[#10B981] text-white shadow-lg shadow-[#10B981]/25'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <CheckCircle2 className="w-4 h-4" />
          Completati
          {completedOrders.length > 0 && (
            <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${
              ordersView === 'completed' ? 'bg-white/20' : 'bg-[#10B981] text-white'
            }`}>
              {completedOrders.length}
            </span>
          )}
        </button>
      </div>

      {/* Active Orders Section */}
      {ordersView === 'active' && (
        <div className="animate-in fade-in-0 slide-in-from-bottom-2 duration-200">
          {activeOrders.length > 0 ? (
              <div className="space-y-4">
                {activeOrders.map((order, index) => (
                  order.order_type === 'cart' || order.items ? (
                    renderCartOrderCard(order, index)
                  ) : (
                    <motion.div
                      key={order.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card className="p-6 hover:shadow-lg transition-all" data-testid={`order-${order.id}`}>
                        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
                          <div className="flex-shrink-0">
                            <CrystalTracker status={order.status} size="medium" />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="text-xl font-bold truncate">{order.title}</h3>
                              <Badge className={getOrderStatusColor(order.status)}>
                                {getOrderStatusLabel(order.status)}
                              </Badge>
                            </div>
                            
                            {order.crafter && (
                              <Link to={crafterUrl(order.crafter)} className="flex items-center gap-2 mb-2 hover:opacity-80 transition-opacity">
                                <div className="w-6 h-6 rounded-full bg-[#008080] flex items-center justify-center text-white text-xs font-bold">
                                  {order.crafter.storefront_title?.[0] || order.crafter.full_name?.[0] || 'A'}
                                </div>
                                <span className="text-sm text-slate-600 hover:text-[#008080]">
                                  {order.crafter.storefront_title || order.crafter.full_name}
                                </span>
                              </Link>
                            )}
                            
                            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <Euro className="w-4 h-4" />
                                €{order.agreed_price}
                              </span>
                              {order.agreed_timeline && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {getTimelineLabel(order.agreed_timeline)}
                                </span>
                              )}
                              {order.tracking_number && (
                                <span className="flex items-center gap-1 bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full">
                                  <Truck className="w-3 h-3" />
                                  {order.tracking_number}
                                </span>
                              )}
                            </div>
                            
                            <div className="flex flex-wrap gap-2 mt-4">
                              <Link to={`/messages?crafter=${order.crafter_id}`}>
                                <Button variant="outline" size="sm">
                                  <MessageSquare className="w-4 h-4 mr-1" />
                                  Messaggia
                                </Button>
                              </Link>
                              
                              {order.status === 'shipped' && (
                                <Button 
                                  size="sm" 
                                  onClick={() => handleMarkDelivered(order)}
                                  disabled={confirmingDelivery === order.id}
                                  className="bg-teal-600 text-white"
                                  data-testid={`mark-delivered-${order.id}`}
                                >
                                  {confirmingDelivery === order.id ? (
                                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                  ) : (
                                    <Package className="w-4 h-4 mr-1" />
                                  )}
                                  Ho ricevuto l'ordine
                                </Button>
                              )}
                              
                              {order.status === 'delivered' && (
                                <Button 
                                  size="sm" 
                                  onClick={() => handleCompleteOrder(order.id)}
                                  className="bg-green-600 text-white"
                                  data-testid={`complete-order-${order.id}`}
                                >
                                  <Star className="w-4 h-4 mr-1" />
                                  Completa
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          <div className="w-full lg:w-auto mt-4 lg:mt-0">
                            <div className="flex items-center justify-between text-xs lg:flex-col lg:items-end lg:gap-1">
                              {['confirmed', 'in_progress', 'shipped', 'delivered'].map((step, idx) => {
                                const steps = ['confirmed', 'in_progress', 'shipped', 'delivered'];
                                const currentIdx = steps.indexOf(order.status);
                                const isCompleted = idx <= currentIdx;
                                const isCurrent = idx === currentIdx;
                                return (
                                  <div 
                                    key={step} 
                                    className={`flex items-center gap-1 ${isCompleted ? 'text-[#008080]' : 'text-slate-300'} ${isCurrent ? 'font-bold' : ''}`}
                                  >
                                    <div className={`w-2 h-2 rounded-full ${isCompleted ? 'bg-[#008080]' : 'bg-slate-200'}`} />
                                    <span className="hidden lg:inline">{getOrderStatusLabel(step)}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                        <OrderHelpBlock order={order} />
                      </Card>
                    </motion.div>
                  )
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center border-dashed border-2">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Package className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">Nessun ordine in corso</h3>
                <p className="text-slate-500 mb-4">Quando effettuerai un acquisto, lo vedrai qui</p>
                <Link to="/products">
                  <Button className="bg-[#008080] hover:bg-[#006666]">
                    Esplora i prodotti
                  </Button>
                </Link>
              </Card>
            )}
        </div>
      )}

      {/* Completed Orders Section */}
      {ordersView === 'completed' && (
        <div className="animate-in fade-in-0 slide-in-from-bottom-2 duration-200">
          {completedOrders.length > 0 ? (
              <div className="space-y-3">
                {completedOrders.map((order, index) => (
                  <motion.div
                    key={order.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.03 }}
                  >
                    {/* Cart orders with multiple products - show each product */}
                    {(order.order_type === 'cart' || order.items) && order.items?.length > 0 ? (
                      <Card className={`p-4 hover:shadow-md transition-shadow bg-gradient-to-r ${order.status === 'cancelled' ? 'from-red-50/50' : 'from-green-50/50'} to-transparent`} data-testid={`completed-order-${order.id}`}>
                        <div className="mb-3 pb-3 border-b border-slate-100">
                          <div className="flex items-center gap-2">
                            <ShoppingBag className={`w-5 h-5 ${order.status === 'cancelled' ? 'text-red-600' : 'text-green-600'}`} />
                            <span className="font-semibold">Ordine {order.order_number || `#${order.id.slice(0,8)}`}</span>
                            {order.status === 'cancelled' ? (
                              <Badge className="bg-red-100 text-red-700 border-red-200">
                                <XCircle className="w-3 h-3 mr-1" />
                                {getOrderStatusLabel(order.status)}
                              </Badge>
                            ) : (
                              <Badge className="bg-green-100 text-green-700 border-green-200">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                {getOrderStatusLabel(order.status)}
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-slate-500 mt-1 flex items-center gap-2">
                            <Calendar className="w-3 h-3" />
                            {new Date(order.cancelled_at || order.completed_at || order.created_at).toLocaleDateString('it-IT', { 
                              day: 'numeric', 
                              month: 'short', 
                              year: 'numeric' 
                            })}
                            <span className="text-slate-400">•</span>
                            <span className={`font-semibold ${order.status === 'cancelled' ? 'text-red-700 line-through' : 'text-green-700'}`}>€{(order.total || 0).toFixed(2)}</span>
                          </div>
                          {order.status === 'cancelled' && order.cancellation_reason && (
                            <p className="text-xs text-red-600 mt-1" data-testid={`completed-order-cancel-reason-${order.id}`}>
                              Motivo: {order.cancellation_reason}
                              {order.cancelled_by && (
                                <span className="text-slate-400"> · annullato {order.cancelled_by === 'client' ? 'da te' : "dall'artigiano"} · rimborso emesso</span>
                              )}
                            </p>
                          )}
                        </div>
                        
                        {/* Individual products */}
                        <div className="space-y-3">
                          {order.items.map((item, itemIdx) => {
                            const isReviewed = reviewedProducts.includes(item.productId);
                            return (
                              <div key={itemIdx} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                                <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0">
                                  {item.image ? (
                                    <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <Package className="w-5 h-5 text-slate-300" />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm truncate">{item.title}</p>
                                  <p className="text-xs text-slate-500">
                                    Qtà: {item.quantity} • €{(item.totalPrice * item.quantity).toFixed(2)}
                                  </p>
                                </div>
                                <div className="flex-shrink-0">
                                  {order.status === 'cancelled' ? null : isReviewed ? (
                                    <div className="flex items-center gap-1 text-green-600 bg-green-50 px-2 py-1 rounded-full text-xs">
                                      <Star className="w-3 h-3 fill-current" />
                                      Recensito
                                    </div>
                                  ) : (
                                    <Button 
                                      variant="outline" 
                                      size="sm" 
                                      className="border-amber-200 text-amber-600 hover:bg-amber-50 text-xs h-7"
                                      onClick={() => handleOpenReviewDialog(order, item)}
                                    >
                                      <Star className="w-3 h-3 mr-1" />
                                      Recensisci
                                    </Button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <OrderHelpBlock order={order} />
                      </Card>
                    ) : (
                      // Single product / Request order
                      <Card className={`p-4 hover:shadow-md transition-shadow bg-gradient-to-r ${order.status === 'cancelled' ? 'from-red-50/50' : 'from-green-50/50'} to-transparent`} data-testid={`completed-order-${order.id}`}>
                        <div className="flex items-center gap-4">
                          {order.status === 'cancelled' ? (
                            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-red-200">
                              <XCircle className="w-7 h-7 text-white" />
                            </div>
                          ) : (
                            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-green-200">
                              <CheckCircle2 className="w-7 h-7 text-white" />
                            </div>
                          )}
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold truncate">{order.title}</h4>
                              {order.status === 'cancelled' ? (
                                <Badge className="bg-red-100 text-red-700 border-red-200">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  {getOrderStatusLabel(order.status)}
                                </Badge>
                              ) : (
                                <Badge className="bg-green-100 text-green-700 border-green-200">
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  {getOrderStatusLabel(order.status)}
                                </Badge>
                              )}
                            </div>
                            {order.order_number && (
                              <span className="text-xs font-mono text-slate-400 block mt-0.5">
                                {order.order_number}
                              </span>
                            )}
                            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 mt-1">
                              {order.crafter && (
                                <Link to={crafterUrl(order.crafter)} className="hover:text-[#008080]">
                                  da {order.crafter.storefront_title || order.crafter.full_name}
                                </Link>
                              )}
                              <span className={`font-semibold ${order.status === 'cancelled' ? 'text-red-700 line-through' : 'text-green-700'}`}>€{order.agreed_price}</span>
                              {(order.cancelled_at || order.completed_at || order.created_at) && (
                                <span className="flex items-center gap-1 text-slate-400">
                                  <Calendar className="w-3 h-3" />
                                  {new Date(order.cancelled_at || order.completed_at || order.created_at).toLocaleDateString('it-IT', { 
                                    day: 'numeric', 
                                    month: 'short', 
                                    year: 'numeric' 
                                  })}
                                </span>
                              )}
                            </div>
                            {order.status === 'cancelled' && order.cancellation_reason && (
                              <p className="text-xs text-red-600 mt-1" data-testid={`completed-order-cancel-reason-${order.id}`}>
                                Motivo: {order.cancellation_reason}
                                {order.cancelled_by && (
                                  <span className="text-slate-400"> · annullato {order.cancelled_by === 'client' ? 'da te' : "dall'artigiano"} · rimborso emesso</span>
                                )}
                              </p>
                            )}
                          </div>
                          
                          <div className="flex-shrink-0">
                            {order.status === 'cancelled' ? null : order.has_review ? (
                              <div className="flex items-center gap-1 text-green-600 bg-green-50 px-3 py-1.5 rounded-full">
                                <Star className="w-4 h-4 fill-current" />
                                <span className="text-sm font-medium">Recensito</span>
                              </div>
                            ) : (
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="border-amber-200 text-amber-600 hover:bg-amber-50"
                                onClick={() => handleOpenReviewDialog(order)}
                              >
                                <Star className="w-4 h-4 mr-1" />
                                Recensisci
                              </Button>
                            )}
                          </div>
                        </div>
                        <OrderHelpBlock order={order} />
                      </Card>
                    )}
                  </motion.div>
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center border-dashed border-2">
                <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <History className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">Nessun ordine completato</h3>
                <p className="text-slate-500">Gli ordini completati appariranno qui</p>
              </Card>
            )}
        </div>
      )}
    </div>
  );
}
